import React, { useEffect, useState, useContext } from 'react';
import { Navigate } from 'react-router';
import PropTypes from 'prop-types';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../System/messageManager/messageManager.js';
import { getOrganizationAccessIcons } from '../../services/userService.js';
import { getAccessCondition } from '../reusable/reusableFunctions.js';
import NoBudgetEmptyState from '../reusable/EmptyState/NoBudgetEmptyState/NoBudgetEmptyState.js';
import { Context } from '../../store/Store.js';
import { APP_ICONS } from '../../static/appIcons.js';
import { NAVIGATION_PATHS } from '../../static/navigationPaths.js';
import { REDUCER_ACTION_TYPES } from '../../store/Reducer.js';
import { useUser } from '../../contexts/userContext.js';
import { useSelection } from '../SelectionContext.js';
import { useNameConventions } from '../../contexts/nameConventionsContext.js';

const PrivateRoute = ({ children, accessIcon }) => {
  const [hasAccess, setHasAccess] = useState(null);
  const [, dispatch] = useContext(Context);
  const { user, costModel } = useUser();
  const { isReady } = useSelection();
  const { nameConventions } = useNameConventions();

  const LOCATIONS_WITHOUT_EMPTY_STATE = [
    ...(costModel ?  [APP_ICONS.BUDGET_SUMMARY] : []),
    APP_ICONS.ORGANIZATIONAL_HIERARCHY,
    APP_ICONS.ORGANIZATION_EDITOR,
    APP_ICONS.MANAGE_ADMIN_USERS,
    APP_ICONS.MANAGE_USERS,
    APP_ICONS.MANAGE_ORGANIZATION_ACCESS,
    APP_ICONS.NAME_CONVENTIONS,
    APP_ICONS.MANAGE_USER_ACCESS,
    APP_ICONS.USER_ACTIVITY,
    APP_ICONS.CONFIGURE_SCORING,
    APP_ICONS.CONFIGURE_INSIGHTS,
    APP_ICONS.CONFIGURE_REQUESTS,
    APP_ICONS.AI_ANALYTICS,
  ];

  useEffect(() => {
    const checkAccess = async () => {
      try {
        if (!isReady) return;
        if (user.activeOrganization == null) {
          setHasAccess(false);
          return;
        }
        const organizationAccessIcons = await getOrganizationAccessIcons();
        dispatch({ type: REDUCER_ACTION_TYPES.ORGANIZATION_ACCESS, payload: organizationAccessIcons.data });
        const access = getAccessCondition(
          accessIcon,
          organizationAccessIcons.data,
          user
        );
        setHasAccess(access);
        if (access === undefined) {
          return <Navigate to={NAVIGATION_PATHS.HOME} />;
        }

        if (access === false) {
          ToastComponent.present({ message: messageManager(nameConventions).ACCESS_DENIED });
        }
      } catch (error) {
        setHasAccess(false);
        ToastComponent.present({ message: messageManager(nameConventions).ACCESS_DENIED });
      }
    };

    checkAccess();
  }, [accessIcon, user]);

  if (hasAccess === null) {
    return null;
  }

  if (!hasAccess) {
    return <Navigate to={`${NAVIGATION_PATHS.BASE_PARAMS}/${NAVIGATION_PATHS.HOME}`} />;
  }

  return user?.activeBudget == null &&
    !LOCATIONS_WITHOUT_EMPTY_STATE.includes(accessIcon) ? (
      <NoBudgetEmptyState />
    ) : (
      children
    );
};

PrivateRoute.propTypes = {
  accessIcon: PropTypes.string,
  children: PropTypes.node,
};

export default PrivateRoute;
